import React, { useEffect, useState } from "react";

import { Lock, Plus, RefreshCw, Unlock } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import { Switch } from "~/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useToast } from "~/hooks/use-toast";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import { MonthlySnapshotService } from "~/services/monthlySnapshotService";

interface MonthlySnapshot {
  id?: string;
  year: number;
  month: number;
  period_label: string;
  total_facturas_count: number;
  total_facturas_amount: number;
  total_credit_notes_count: number;
  total_credit_notes_amount: number;
  net_amount: number;
  cross_period_adjustments: number;
  is_locked: boolean;
  locked_at?: string;
  locked_by?: string;
  snapshot_created_at?: string;
  snapshot_created_by?: string;
  last_updated_at?: string;
  last_updated_by?: string;
}

const MonthlySnapshotsManagement: React.FC = () => {
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const [snapshots, setSnapshots] = useState<MonthlySnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingOperations, setLoadingOperations] = useState<
    Record<string, boolean>
  >({});

  // Manual input dialog state
  const [isManualDialogOpen, setIsManualDialogOpen] = useState(false);
  const [editingSnapshot, setEditingSnapshot] =
    useState<MonthlySnapshot | null>(null);
  const [manualFormData, setManualFormData] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    total_facturas_count: 0,
    total_facturas_amount: 0,
    total_credit_notes_count: 0,
    total_credit_notes_amount: 0,
    net_amount: 0,
    cross_period_adjustments: 0,
    is_locked: false,
  });

  const isAdmin = user.role === "admin" || user.role === "superadmin";
  const isSuperAdmin = user.role === "superadmin";

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-CL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const loadSnapshots = async () => {
    if (!isAdmin) return;

    setLoading(true);
    try {
      const data = await MonthlySnapshotService.getAllSnapshots();
      setSnapshots(data);
    } catch (error) {
      console.error("Error loading snapshots:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los snapshots mensuales.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Initialize manual form data
  const initializeManualForm = (snapshot?: MonthlySnapshot) => {
    if (snapshot) {
      setEditingSnapshot(snapshot);
      setManualFormData({
        year: snapshot.year,
        month: snapshot.month,
        total_facturas_count: snapshot.total_facturas_count,
        total_facturas_amount: snapshot.total_facturas_amount,
        total_credit_notes_count: snapshot.total_credit_notes_count,
        total_credit_notes_amount: snapshot.total_credit_notes_amount,
        net_amount: snapshot.net_amount,
        cross_period_adjustments: snapshot.cross_period_adjustments,
        is_locked: snapshot.is_locked,
      });
    } else {
      setEditingSnapshot(null);
      setManualFormData({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        total_facturas_count: 0,
        total_facturas_amount: 0,
        total_credit_notes_count: 0,
        total_credit_notes_amount: 0,
        net_amount: 0,
        cross_period_adjustments: 0,
        is_locked: false,
      });
    }
  };

  // Handle manual form submission
  const handleManualSubmit = async () => {
    if (!isAdmin) return;

    try {
      const periodLabel = `${manualFormData.year}-${manualFormData.month.toString().padStart(2, "0")}`;

      const snapshotData = {
        year: manualFormData.year,
        month: manualFormData.month,
        period_label: periodLabel,
        total_facturas_count: manualFormData.total_facturas_count,
        total_facturas_amount: manualFormData.total_facturas_amount,
        total_credit_notes_count: manualFormData.total_credit_notes_count,
        total_credit_notes_amount: manualFormData.total_credit_notes_amount,
        net_amount: manualFormData.net_amount,
        cross_period_adjustments: manualFormData.cross_period_adjustments,
        is_locked: manualFormData.is_locked,
      };

      if (editingSnapshot) {
        // Update existing snapshot
        await MonthlySnapshotService.updateSnapshotManually(
          editingSnapshot.id!,
          snapshotData
        );
        toast({
          title: "Snapshot actualizado",
          description: `El snapshot de ${periodLabel} fue actualizado manualmente.`,
        });
      } else {
        // Create new snapshot
        await MonthlySnapshotService.createSnapshotManually(snapshotData);
        toast({
          title: "Snapshot creado",
          description: `El snapshot de ${periodLabel} fue creado manualmente.`,
        });
      }

      setIsManualDialogOpen(false);
      await loadSnapshots();
    } catch (error) {
      console.error("Error saving manual snapshot:", error);
      toast({
        title: "Error",
        description: "No se pudo guardar el snapshot manual.",
        variant: "destructive",
      });
    }
  };

  // Calculate net amount when form data changes
  const calculatedNetAmount =
    manualFormData.total_facturas_amount -
    manualFormData.total_credit_notes_amount +
    manualFormData.cross_period_adjustments;

  useEffect(() => {
    if (isAdmin) {
      loadSnapshots();
    }
  }, [isAdmin]);

  // Create missing snapshots
  const handleCreateMissingSnapshots = async () => {
    if (!isAdmin) return;

    setLoadingOperations((prev) => ({ ...prev, createMissing: true }));
    try {
      const createdSnapshots =
        await MonthlySnapshotService.createMissingSnapshots();

      toast({
        title: "Snapshots creados",
        description: `Se crearon ${createdSnapshots.length} snapshots mensuales faltantes.`,
      });

      // Reload the list
      await loadSnapshots();
    } catch (error) {
      console.error("Error creating missing snapshots:", error);
      toast({
        title: "Error",
        description: "No se pudieron crear los snapshots faltantes.",
        variant: "destructive",
      });
    } finally {
      setLoadingOperations((prev) => ({ ...prev, createMissing: false }));
    }
  };

  // Update a specific snapshot
  const handleUpdateSnapshot = async (year: number, month: number) => {
    if (!isAdmin) return;

    const key = `${year}-${month}`;
    setLoadingOperations((prev) => ({ ...prev, [key]: true }));

    try {
      await MonthlySnapshotService.createOrUpdateSnapshot(year, month, true);

      toast({
        title: "Snapshot actualizado",
        description: `El snapshot de ${year}-${month.toString().padStart(2, "0")} fue actualizado.`,
      });

      // Reload the list
      await loadSnapshots();
    } catch (error) {
      console.error("Error updating snapshot:", error);
      toast({
        title: "Error",
        description: `No se pudo actualizar el snapshot de ${year}-${month.toString().padStart(2, "0")}.`,
        variant: "destructive",
      });
    } finally {
      setLoadingOperations((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Lock a month
  const handleLockMonth = async (year: number, month: number) => {
    if (!isAdmin) return;

    const key = `lock-${year}-${month}`;
    setLoadingOperations((prev) => ({ ...prev, [key]: true }));

    try {
      await MonthlySnapshotService.lockMonth(year, month);

      toast({
        title: "Mes bloqueado",
        description: `El mes ${year}-${month.toString().padStart(2, "0")} fue bloqueado. Los totales son ahora inmutables.`,
      });

      // Reload the list
      await loadSnapshots();
    } catch (error) {
      console.error("Error locking month:", error);
      toast({
        title: "Error",
        description: `No se pudo bloquear el mes ${year}-${month.toString().padStart(2, "0")}.`,
        variant: "destructive",
      });
    } finally {
      setLoadingOperations((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Unlock a month (super admin only)
  const handleUnlockMonth = async (year: number, month: number) => {
    if (!isSuperAdmin) return;

    const key = `unlock-${year}-${month}`;
    setLoadingOperations((prev) => ({ ...prev, [key]: true }));

    try {
      await MonthlySnapshotService.unlockMonth(year, month);

      toast({
        title: "Mes desbloqueado",
        description: `El mes ${year}-${month.toString().padStart(2, "0")} fue desbloqueado. Los totales pueden ser modificados.`,
      });

      // Reload the list
      await loadSnapshots();
    } catch (error) {
      console.error("Error unlocking month:", error);
      toast({
        title: "Error",
        description: `No se pudo desbloquear el mes ${year}-${month.toString().padStart(2, "0")}.`,
        variant: "destructive",
      });
    } finally {
      setLoadingOperations((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">
          No tienes permisos para acceder a esta página.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[100vw] px-6 md:max-w-[60vw] md:px-0 lg:max-w-[70vw] xl:max-w-[75vw]">
      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            Snapshots Mensuales
          </h2>
          <p className="text-muted-foreground">
            Gestión de totales mensuales inmutables de facturas
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="mb-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Snapshots
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{snapshots.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Meses Bloqueados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {snapshots.filter((s) => s.is_locked).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Meses Activos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {snapshots.filter((s) => !s.is_locked).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Neto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(
                snapshots.reduce((sum, s) => sum + s.net_amount, 0)
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex flex-wrap gap-2">
        <Button
          onClick={handleCreateMissingSnapshots}
          disabled={loadingOperations.createMissing || loading}
          variant="outline"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          {loadingOperations.createMissing ? "Creando..." : "Crear Faltantes"}
        </Button>

        <Dialog open={isManualDialogOpen} onOpenChange={setIsManualDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => initializeManualForm()} variant="outline">
              <Plus className="mr-2 h-4 w-4" />
              Crear Manual
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingSnapshot ? "Editar Snapshot" : "Crear Snapshot Manual"}
              </DialogTitle>
              <DialogDescription>
                {editingSnapshot
                  ? "Modifica los valores del snapshot existente."
                  : "Crea un nuevo snapshot con valores personalizados."}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="year">Año</Label>
                  <Input
                    id="year"
                    type="number"
                    value={manualFormData.year}
                    onChange={(e) =>
                      setManualFormData((prev) => ({
                        ...prev,
                        year: parseInt(e.target.value) || 0,
                      }))
                    }
                    min="2020"
                    max="2030"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="month">Mes</Label>
                  <Select
                    value={manualFormData.month.toString()}
                    onValueChange={(value) =>
                      setManualFormData((prev) => ({
                        ...prev,
                        month: parseInt(value),
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(
                        (month) => (
                          <SelectItem key={month} value={month.toString()}>
                            {month.toString().padStart(2, "0")}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="facturas_count">Cantidad de Facturas</Label>
                <Input
                  id="facturas_count"
                  type="number"
                  value={manualFormData.total_facturas_count}
                  onChange={(e) =>
                    setManualFormData((prev) => ({
                      ...prev,
                      total_facturas_count: parseInt(e.target.value) || 0,
                    }))
                  }
                  min="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="facturas_amount">Monto Total Facturas</Label>
                <Input
                  id="facturas_amount"
                  type="number"
                  value={manualFormData.total_facturas_amount}
                  onChange={(e) =>
                    setManualFormData((prev) => ({
                      ...prev,
                      total_facturas_amount: parseInt(e.target.value) || 0,
                    }))
                  }
                  min="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="credit_notes_count">
                  Cantidad Notas de Crédito
                </Label>
                <Input
                  id="credit_notes_count"
                  type="number"
                  value={manualFormData.total_credit_notes_count}
                  onChange={(e) =>
                    setManualFormData((prev) => ({
                      ...prev,
                      total_credit_notes_count: parseInt(e.target.value) || 0,
                    }))
                  }
                  min="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="credit_notes_amount">
                  Monto Total Notas de Crédito
                </Label>
                <Input
                  id="credit_notes_amount"
                  type="number"
                  value={manualFormData.total_credit_notes_amount}
                  onChange={(e) =>
                    setManualFormData((prev) => ({
                      ...prev,
                      total_credit_notes_amount: parseInt(e.target.value) || 0,
                    }))
                  }
                  min="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="net_amount">Monto Neto (Calculado)</Label>
                <Input
                  id="net_amount"
                  type="number"
                  value={calculatedNetAmount}
                  disabled
                  className="bg-muted"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_locked"
                  checked={manualFormData.is_locked}
                  onCheckedChange={(checked) =>
                    setManualFormData((prev) => ({
                      ...prev,
                      is_locked: checked,
                    }))
                  }
                />
                <Label htmlFor="is_locked">Bloquear período</Label>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsManualDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="button" onClick={handleManualSubmit}>
                {editingSnapshot ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Snapshots Table */}
      <Card>
        <CardHeader>
          <CardTitle>Snapshots Mensuales</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <TableSkeleton rows={6} columns={6} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Período</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Facturas</TableHead>
                  <TableHead className="text-right">Monto Neto</TableHead>
                  <TableHead className="text-right">
                    Última Actualización
                  </TableHead>
                  <TableHead className="text-center">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {snapshots.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      No hay snapshots mensuales creados.
                    </TableCell>
                  </TableRow>
                )}
                {snapshots.map((snapshot, index) => {
                  const updateKey = `${snapshot.year}-${snapshot.month}`;
                  const lockKey = `lock-${snapshot.year}-${snapshot.month}`;
                  const unlockKey = `unlock-${snapshot.year}-${snapshot.month}`;

                  return (
                    <TableRow
                      key={`${snapshot.year}-${snapshot.month}`}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    >
                      <TableCell className="font-medium">
                        {snapshot.period_label}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {snapshot.is_locked ? (
                            <>
                              <Lock className="h-4 w-4 text-red-500" />
                              <span className="text-sm text-red-600">
                                Bloqueado
                              </span>
                            </>
                          ) : (
                            <>
                              <Unlock className="h-4 w-4 text-yellow-500" />
                              <span className="text-sm text-yellow-600">
                                Activo
                              </span>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div>
                          <div className="font-medium">
                            {snapshot.total_facturas_count}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        {formatCurrency(snapshot.net_amount)}
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {snapshot.last_updated_at
                          ? formatDate(snapshot.last_updated_at)
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleUpdateSnapshot(
                                      snapshot.year,
                                      snapshot.month
                                    )
                                  }
                                  disabled={loadingOperations[updateKey]}
                                >
                                  <RefreshCw className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Actualizar snapshot con datos actuales</p>
                              </TooltipContent>
                            </Tooltip>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    initializeManualForm(snapshot);
                                    setIsManualDialogOpen(true);
                                  }}
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Editar valores manualmente</p>
                              </TooltipContent>
                            </Tooltip>

                            {!snapshot.is_locked ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      handleLockMonth(
                                        snapshot.year,
                                        snapshot.month
                                      )
                                    }
                                    disabled={loadingOperations[lockKey]}
                                  >
                                    <Lock className="h-3 w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Bloquear período para evitar cambios</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : isSuperAdmin ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      handleUnlockMonth(
                                        snapshot.year,
                                        snapshot.month
                                      )
                                    }
                                    disabled={loadingOperations[unlockKey]}
                                  >
                                    <Unlock className="h-3 w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Desbloquear período (solo superadmin)</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : null}
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MonthlySnapshotsManagement;
